import { useState } from 'react'
import { Panel as ResizablePanel } from 'react-resizable-panels'
import type { TenantBootstrapModel } from '@unified-lk/tenant-config'
import type { AuthSession } from '../../../types/plans'
import { getServices } from './constructorTypes'
import type { ConstructorPlan, ConstructorStage } from './constructorTypes'
import { InstallmentPanel } from './panels/InstallmentPanel'
import { PublicThemePanel } from './panels/PublicThemePanel'
import { PublicOfferPanel } from './panels/PublicOfferPanel'
import { PlanNotePanel } from './panels/PlanNotePanel'
import { PlanCashOfferPanel } from './panels/PlanCashOfferPanel'
import { PlanAnalyticsPanel } from './panels/PlanAnalyticsPanel'
import { SharePanel } from './panels/SharePanel'
import { MediaPanel } from './panels/MediaPanel'
import { DiagnocatPatientCardWizard } from './diagnostics/DiagnocatPatientCardWizard'
import { usePatientDiagnosticsLinked } from './diagnostics/usePatientDiagnosticsLinked'
import { useConstructorWorkflow } from './shell/ConstructorWorkflowContext'
import { StaffSegmentTabs } from '../../../shared/staff-ui'

type RightDrawerTab = 'sharing' | 'analytics' | 'diagnostics'

export function ConstructorRightDrawer({
  planDetail,
  stages,
  totalAmount,
  bootstrap,
  session,
  lkPatientId,
  misPatientId,
  staffUserId,
  onReload,
  onMisPlansRefresh,
  onClose,
}: {
  planDetail: ConstructorPlan
  stages: ConstructorStage[]
  totalAmount: number
  bootstrap: TenantBootstrapModel
  session: AuthSession
  lkPatientId: number | null
  misPatientId: string | null
  staffUserId: string
  onReload: () => void
  onMisPlansRefresh?: (misPlanId: string | null) => void
  onClose: () => void
}) {
  const { openWindow } = useConstructorWorkflow()
  const [activeTab, setActiveTab] = useState<RightDrawerTab>('sharing')

  const diagnosticsLink = usePatientDiagnosticsLinked({
    bootstrap,
    session,
    lkPatientId,
    misPatientId,
    enabled: Boolean(lkPatientId),
  })

  return (
    <ResizablePanel
      id="right-drawer"
      defaultSize="22%"
      minSize="14%"
      maxSize="45%"
      className="min-h-0 overflow-y-auto border-l border-slate-200 bg-slate-50 p-3"
    >
      <div className="space-y-3">
        <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
          <div className="mb-3 flex items-center justify-between gap-2">
            <StaffSegmentTabs
              tabs={[
                { id: 'sharing', label: 'Шеринг' },
                { id: 'analytics', label: 'Аналитика' },
                { id: 'diagnostics', label: 'КТ/AI' },
              ]}
              activeTab={activeTab}
              onChange={setActiveTab}
            />
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              title="Свернуть панель"
              aria-label="Свернуть панель"
            >
              ✕
            </button>
          </div>

          <dl className="mb-3 grid grid-cols-3 gap-2 text-xs">
            <div className="rounded-lg bg-slate-50 px-2 py-1.5">
              <dt className="text-slate-500">Этапов</dt>
              <dd className="font-semibold text-slate-800">{stages.length}</dd>
            </div>
            <div className="rounded-lg bg-slate-50 px-2 py-1.5">
              <dt className="text-slate-500">Услуг</dt>
              <dd className="font-semibold text-slate-800">{stages.flatMap(getServices).length}</dd>
            </div>
            <div className="rounded-lg bg-slate-50 px-2 py-1.5">
              <dt className="text-slate-500">Итого</dt>
              <dd className="font-semibold text-slate-800">{totalAmount.toLocaleString('ru-RU')} ₽</dd>
            </div>
          </dl>

          {activeTab === 'sharing' ? (
            <div className="space-y-3">
              <SharePanel plan={planDetail} bootstrap={bootstrap} session={session} onReload={onReload} />
              <InstallmentPanel plan={planDetail} bootstrap={bootstrap} session={session} onReload={onReload} />
              <PublicThemePanel plan={planDetail} bootstrap={bootstrap} session={session} onReload={onReload} />
              <PublicOfferPanel plan={planDetail} bootstrap={bootstrap} session={session} onReload={onReload} />
              <PlanNotePanel plan={planDetail} bootstrap={bootstrap} session={session} onReload={onReload} />
              <PlanCashOfferPanel plan={planDetail} bootstrap={bootstrap} session={session} onReload={onReload} />
              <MediaPanel plan={planDetail} bootstrap={bootstrap} session={session} onReload={onReload} />
            </div>
          ) : null}

          {activeTab === 'analytics' ? (
            <PlanAnalyticsPanel plan={planDetail} />
          ) : null}

          {activeTab === 'diagnostics' ? (
            <div className="space-y-3">
              <p className="text-xs leading-5 text-slate-500">
                Справочник КТ, связка Diagnocat и AI-сценарии для составления плана.
              </p>
              <div className="flex flex-wrap gap-1.5">
                <button type="button" className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-[10px] text-slate-700 hover:bg-white" onClick={() => openWindow('plan-review')}>
                  Черновик / этапы
                </button>
                <button type="button" className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-[10px] text-slate-700 hover:bg-white" onClick={() => openWindow('plan-voice-fill')}>
                  Голос / текст
                </button>
                <button type="button" className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-[10px] text-slate-700 hover:bg-white" onClick={() => openWindow('plan-auto-distribute')}>
                  Разнесение
                </button>
                <button type="button" className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-[10px] text-slate-700 hover:bg-white" onClick={() => openWindow('plan-presentation')}>
                  Gamma
                </button>
              </div>
              <DiagnocatPatientCardWizard
                bootstrap={bootstrap}
                session={session}
                plan={planDetail}
                lkPatientId={lkPatientId}
                misPatientId={misPatientId}
                staffUserId={staffUserId}
                onApplied={onReload}
                onMisPlansRefresh={onMisPlansRefresh}
                linkSnapshot={diagnosticsLink}
                onAnalysisReady={() => {
                  void diagnosticsLink.reloadDiagnocat()
                }}
              />
            </div>
          ) : null}
        </div>
      </div>
    </ResizablePanel>
  )
}
